/* Dynamic Plot code copyright 2018 (c) Tuva Labs Inc. */
/**
 * Plot Function Element: model and controller for a function line/curve in a plot
 * Added to Dyn.PlotShaper's list of plot elements, to make a plot.
 * created by cmiller
 */

import { PlotElement } from './plotElement'
import Raphael from "raphael";

//============================
export const wrapDynPlotFunctionElement = function( Dyn ) {
    let mediator = Dyn.mediator;
    const kHoverPointIndex = 0,
        kClickPointIndex = 1,
        kYInterceptPointIndex = 2;

    // roots and other calculated point indexes are 2+

    class FunctionPointData {
        constructor( x, y, pointIndex ) {
            this.wantVisible = false;
            this.isValid = false; // data and view points are valid
            this.isHoverPoint = (pointIndex === kHoverPointIndex);
            this.isClickPoint = (pointIndex === kClickPointIndex);
            this.isIntercept = (pointIndex >= kYInterceptPointIndex);
            this.dataX = x;
            this.dataY = y;
            this.viewX = this.viewY = -1; // location while hidden or invalid
            this.display = null; // {x,y} display values if valid point
            this.el = null; // reference to Raphael element
        }

        copyData( otherPt ) {
            this.wantVisible = otherPt.wantVisible;
            this.isValid = otherPt.isValid
            this.dataX = otherPt.dataX;
            this.dataY = otherPt.dataY;
            this.viewX = otherPt.viewX;
            this.viewY = otherPt.viewY;
            this.display = null; // {x,y} display values if valid point
        }

        updateViewCoords( xAxisView, yAxisView ) {
            this.isValid = Dyn.mathUtil.isFinite( this.dataY );
            if( this.isValid ) {
                this.viewX = xAxisView.dataToPixel( this.dataX );
                this.viewY = yAxisView.dataToPixel( this.dataY );
            }
            delete this.display; // old display strings out of date
        }

        setElement( circleElement ) {
            // cross-reference from point data to Raphael element and back
            this.el = circleElement;
            circleElement.data( "pointData", this );
        }

        isElementValidOrNull() {
            // return true if point data to raphael element link is correct
            let isNull = !(this.el);
            return isNull || (this === this.el.data( "pointData" ));
        }

        show() {
            this.wantVisible = true;
            if( this.el ) {
                this.el.show();
                this.el.toFront();
            }
        }

        hide() {
            this.wantVisible = false;
            if( this.el ) {
                this.el.hide();
            }
        }

        isChangedPoint( element ) {
            // true if our point data not the same as the visible circle element
            let isVisible = Dyn.plotUtil.isElementVisible( element ),
                ex = element.attr( "cx" ),
                ey = element.attr( "cy" );
            return (isVisible != this.wantVisible ||
                ex !== this.viewX ||
                ey !== this.viewY);
        }

        updateVisibility( isSelectedFormula ) {
            let showIt;
            if( this.isIntercept ) {
                showIt = isSelectedFormula && this.isValid; // show valid intercepts of selected formula
            } else {
                showIt = isSelectedFormula && this.wantVisible; // maintain visibility of others if selected formula
            }
            if( showIt ) {
                this.show();
            } else {
                this.hide();
            }
        }

        updateDisplay( xAttr, yAttr, xPrecision, yPrecision ) {
            // update string values for Summary View or hover over point
            this.display = { // works even if invalid point
                x: xAttr.axisValueToString( this.dataX, xPrecision ),
                y: yAttr.axisValueToString( this.dataY, yPrecision )
            };
        }
    }

    class FormulaRange {
        constructor() {
            this.evalX = null; // new Dyn.mathUtil.Range( x, x );
            this.evalY = null; // new Dyn.mathUtil.Range( y, y );
            this.stringX = "";
            this.stringY = "";
            this.countFinite = 0; // finite points
            this.countNaN = 0;    // undefined points (have x but no y)
            this.countAll = 0;    // total number of points evaluated
            this.hasDiscontinuity = false;
            this.lastMessage = "";
        }

        /** Add a point on the curve to expand the given formula's computed numeric range */
        addToRange( x, y, hasDiscontinuity ) {

            if( !this.evalX ) {
                // all points invalid until now, create first point
                this.evalX = new Dyn.mathUtil.Range( x, x );
                this.evalY = new Dyn.mathUtil.Range( y, y );
            } else {
                this.evalX.addPoint( x );
                this.evalY.addPoint( y );
            }
            this.countFinite++;
            this.countAll++;
            if( hasDiscontinuity ) {
                this.hasDiscontinuity = true; // set once and remember
            }
        }

        /** record point on curve which does not evaluate to a defined number */
        addUndefinedPoint() {
            this.countNaN++;
            this.countAll++;
        }

        /** Update the given formula's range string. */
        formatRange( xAxisView, yAxisView ) {
            function formatIt( attribute, range, axis ) {
                let str = "";
                if( attribute && range && range.isFinite() ) {
                    str = Dyn.loc( "{{min}} ≤ {{axis}} ≤ {{max}}", {
                        min: attribute.axisValueToString( range.min, attribute.getNumericDecimals() ),
                        max: attribute.axisValueToString( range.max, attribute.getNumericDecimals() ),
                        axis: axis
                    } );
                }
                return str;
            }

            let xAttr = xAxisView.getAttribute(),
                yAttr = yAxisView.getAttribute();
            this.stringX = formatIt( xAttr, this.evalX, 'x' );
            this.stringY = formatIt( yAttr, this.evalY, 'y' );
        }
    } // class FormulaRange
    //Dyn.FormulaRange = FormulaRange;

    class PlotFunctionElement extends PlotElement {
        /**
         * Dyn.PlotFunctionElement()
         * Create a plot element to hold one or more plottable functions.
         * If no parameters are given, it creates a single default formula
         * @param [formulaID] -- optional formula ID
         * @param [formulaString] -- optional formula string
         * @param [formulaSyntax] -- optional formula syntax, see setFormula() for allowable types
         * @param [constants]
         * @constructor
         */
        constructor( formulaID, formulaString, formulaSyntax, constants, lineColor ) {
            super( Dyn );
            "use strict";
            this.initElement( {
                priority: Dyn.EPlotPriority.kPriorityPlotFunction,
                replaceType: Dyn.EPlotControl.kControlsPlotFunction,
                primaryType: Dyn.EPlotControl.kControlsPlotFunction,
                otherControls: [
                    Dyn.EPlotControl.kControlsAdornmentPositionOnly
                ]
            }, Dyn );

            this.formulas = [];             // array of Dyn.Formula objects, usually (but not always) matches the number of Paths
            this.selectedFormulaIndex = -1; // index into this.getAdornmentsArray() or -1 if nothing selected
            this.isInPlottableAxes = false; // true if in plot with two numeric axes (e.g. Scatterplot), otherwise false.
            this.plotRange = {};            // numeric range data for overall plot and all evaluated formulas, see initRange()
            this.formulaRanges = [];        // numeric range data for each specific formula, see initRange()
            this.messages = [];             // formula's evaluated error and range messages, one per formula object
            this.formulaSyntax = 'LaTeX';   // constant

            if( typeof formulaID === "string" || typeof formulaID === "number" ) {
                this.setFormula( formulaID, formulaString, formulaSyntax, constants, lineColor );
            }
        }

        get kUndoAction() {
            return 'Add Function';
        }

        static debugLogEvalCount = true; // turn on for debugging, 2 console.log lines per function eval
        static debugLogEveryEval = false; // turn on for debugging 1 console.log line per point on curve

        /**
         * Save the PlotElement to a data object that can be JSONified.
         * @returns {{}} - data to be used by restore()
         */
        save( optionalAllFunctionsState ) {
            "use strict";
            // NOTE: when save is used for undo/redo of Model Function changes, we allow the Model Function
            // component to pass in the correct state, since it may have changed before being passed on to the plot to save.
            let functionState = optionalAllFunctionsState || Dyn.FunctionUtil.copyAllFunctions();
            Dyn.assert( functionState );
            Dyn.assert( typeof functionState.formulaString === "undefined", "PlotFunctionElement.save(): invalid parameter, use copyAllFunctions() instead of copyFunction()" );
            let plotElementModel = {
                primaryType: this.primaryType,
                plotFunctions: functionState
            };

            return plotElementModel;
        }

        /**
         * Change our PlotElement to match the saved data.
         */
        restore( savedData ) {
            "use strict";
            var plotFunctions = this;
            if( savedData && savedData.plotFunctions ) {

                // restore formulas (caller responsible for recalculating plot via Dyn.PlotView)
                this.selectedFormulaIndex = -1;
                this.formulas = []; // erase old
                this.messages = []; // erase old
                for( var key in savedData.plotFunctions ) {
                    let f = savedData.plotFunctions[key];
                    let constants = Dyn.FunctionUtil.getConstantsFromSliderVars( f );
                    this.setFormula( key, f.formulaString, "LaTeX", constants, f.lineColor );
                }
                // restore formula store and re-populate Model Functions panel if visible
                Dyn.FunctionUtil.restoreAllFunctions( savedData.plotFunctions );
            } else {
                Dyn.FunctionUtil.deleteAllFunctions();
            }
        }

        /** Destroy a Dyn.PlotElement */
        destroy() {
            // clear Model Functions'
            // if part of a Reset/PlotView.mixUp(), parent should have called "toolBar:closeModelFunctions"
            // functions should already be saved with this.restore() undo.
            Dyn.FunctionUtil.deleteAllFunctions();

            super.destroy();
            Dyn.assert( this.plotShaper === null, "Dyn.PlotElement.destroy was not called" );
        }

        get kNumAdornmentsPerLine() {
            return 1; // each function has one paper.set() that has 2+ elements
        }

        get buttons() {
            return {
                // set the constants to lay out the square buttons
                kNumButtons: 4,

                button0top: Dyn.plotUtil.mapOrFxButtonTop,
                radius: Dyn.plotUtil.mapButtonRadius,
                width: Dyn.plotUtil.mapButtonWidth,
                height: Dyn.plotUtil.mapButtonWidth,
                spacing: Dyn.plotUtil.mapButtonSpacer,
                left: Dyn.plotUtil.mapButtonleft,

                top: function( buttonNum ) {
                    return this.button0top + (buttonNum * (this.spacing + this.height))
                },
                bottom: function( buttonNum ) {
                    return this.top( buttonNum ) + this.height;
                },
                marginForButtons: function() {
                    const lastButtonNum = this.kNumButtons - 1,
                        yAxisTop = 20;
                    return this.bottom( lastButtonNum ) - yAxisTop;
                }
            };
        }

        // curly added by craig while talking... I think it's a mistake would close class PlotFunctionElement

        _desiredNumberOfAdornments() {
            return (this.isInPlottableAxes ? (this.buttons.kNumButtons + (this.formulas.length * this.kNumAdornmentsPerLine)) : 0);
        }

        _getPathSet( adornmentArray, formulaIndex ) {
            // one set per fx line/curve, contains visible elements on line (e.g. intercept points)
            return adornmentArray[this.buttons.kNumButtons + (formulaIndex * this.kNumAdornmentsPerLine)];
        }

        _getPathElement( adornmentArray, formulaIndex ) {
            // visible path for fx line/curve
            let pathSet = adornmentArray[this.buttons.kNumButtons + (formulaIndex * this.kNumAdornmentsPerLine)];
            return pathSet ? pathSet[0] : null;
        }

        _getCoverElement( adornmentArray, formulaIndex ) {
            // invisible wide click/hover area over fx line/curve
            let pathSet = adornmentArray[this.buttons.kNumButtons + (formulaIndex * this.kNumAdornmentsPerLine)];
            return pathSet ? pathSet[1] : null;
        }

        _getPointElement( adornmentArray, formulaIndex, pointIndex ) {
            // visible or hidden point along fx line/curve
            let pathSet = adornmentArray[this.buttons.kNumButtons + (formulaIndex * this.kNumAdornmentsPerLine)];
            return pathSet ? pathSet[2 + pointIndex] : null;
        }

        /** Update the plot's axis model, removing any elements incompatible with this PlotElement. */
        calcAxis( ioWhatChanged, subViewRef ) {
            "use strict";
            var xAxisView = subViewRef.xAxisView,
                yAxisView = subViewRef.yAxisView;

            // plot functions only if both axis are numerical
            this.isInPlottableAxes = (xAxisView.isNumberLineAxis() &&
                yAxisView.isNumberLineAxis());
            //  && xAxisView.numeric.axisRange

            if( this.isInPlottableAxes ) { // if buttons visible
                // move Y axis title to the side so it does not overlap with buttons
                yAxisView.setMarginForButtons( this.buttons.marginForButtons() );
            }
        }

        /**
         * Find the nearest point on the function line, in view coordinates.
         * If nearest not found, return an invalid point (.isValid===false)
         * @param otherXY -- point on or off of line
         * @param width -- initial +/- search area in x-axis
         * @param fx -- y=fx(x) function (in view coordinates)
         * @returns {FunctionPointData}
         */
        findNearestPointOnFx( otherXY, fx, lastPointData ) {
            function calcDistanceSquared( p, xy ) {
                // Note: squares of distances can be safely compared, avoids sqrt() calculation
                let dx = p.viewX - xy.x,
                    dy = p.viewY - xy.y;
                return (dx * dx) + (dy * dy);
            }

            function calcFxAndError( viewX, xy ) {
                // find point on curve with save viewX
                let p = fx( viewX ); // get point on curve with .dataX,.dataY, .viewX, .viewY
                if( p.isValid ) {
                    p.error = calcDistanceSquared( p, xy );
                } else {
                    // if point invalid, return large error so point is not nearest
                    p.error = Dyn.mathUtil.MAX_SAFE_INTEGER;
                }
                return p;
            }

            function linearSearchForValidPt( pGood, pBad ) {
                // find a valid point between pGood and pBad fall back to pGood as worst case
                let x1 = pGood.viewX,
                    x2 = pBad.viewX,
                    pLastGood = pGood,
                    pNext = pGood,
                    step = (x2 - x1) / 10; // step may be positive or negative
                Dyn.assert( pGood.isValid );
                for( let xi = x1 + step; (((step > 0) ? (xi < x2) : (xi > x2)) && pNext.isValid); xi += step ) {
                    pNext = calcFxAndError( xi, otherXY );
                    if( pNext.isValid ) {
                        pLastGood = pNext;
                    }
                    //console.log( "linearSearchForValidPt", xi, pNext.isValid );
                }
                Dyn.assert( pLastGood.isValid );
                return pLastGood;
            }

            function findTwoValidPoints( p0, p1, p2 ) {
                // if any points invalid, do linear search to try to find 2 good points
                if( p0.isValid ) {
                    if( p1.isValid && !p2.isValid ) {
                        p2 = linearSearchForValidPt( p0, p2 );
                    } else if( p2.isValid && !p1.isValid ) {
                        p1 = linearSearchForValidPt( p0, p1 );
                    }
                } else {
                    if( p1.isValid && !p2.isValid ) {
                        p2 = linearSearchForValidPt( p1, p0 );
                    } else if( p2.isValid && !p1.isValid ) {
                        p1 = linearSearchForValidPt( p2, p0 );
                    }
                }
            }

            // Uses stepwise approximation to find the nearest point on curve.
            //      Search area is p0 +/- width.
            //      p0 = [ fx(otherXY.x), otherXY.x ]
            //      width = 2*(distance( p0, otherXY )
            // Alternative algorithm #1 would measure the length of the perpendicular of the line segments of the curve.
            // Alternative algorithm #2 would be to have direct formula for perpendicular values of curve.
            const targetError = 1; // loop until our measured width is within x pixels of actual closest point
            const usePerpendicularInX = true;

            let p0 = calcFxAndError( otherXY.x, otherXY ), // point on line with same x as otherXY.x
                width = Math.sqrt( p0.error ), // initial search area, if p0 valid
                pLast = lastPointData.wantVisible ? calcFxAndError( lastPointData.viewX, otherXY ) : null;
            if( usePerpendicularInX ) {
                return p0; // skip the fancy stuff, just get x value on curve.
            }
            if( pLast && (pLast.error < p0.error) ) {
                width = Math.sqrt( pLast.error ); // initial search area, if pLast valid and closer
            }
            let p1 = calcFxAndError( otherXY.x - width, otherXY ),
                p2 = calcFxAndError( otherXY.x + width, otherXY );
            findTwoValidPoints( p0, p1, p2 );
            // now do search between p1 and p2,
            //      step either p1 or p2, 1/2 of distance each time,
            //      until width within target
            while( width > targetError && (p1.isValid || p2.isValid) ) {
                // step either p1 or p2 in, which ever is farther from otherXY
                if( p1.error > p2.error ) {
                    p1 = calcFxAndError( p2.viewX - width, otherXY ); // move p1.x up 1/2 way to p2
                } else {
                    p2 = calcFxAndError( p1.viewX + width, otherXY ); // move p2.x down 1/2 way to p1
                }
                width /= 2; // next time half the dist, so p1 < p2 always
            }
            if( p1.isValid && p2.isValid ) {
                return fx( (p1.viewX + p2.viewX) / 2 ); // mid-point of p1 and p2
            }
            // one or more invalid points because fx() returns invalid points near otherXY; try to return a valid one if it exists.
            return (p1.isValid ? p1 : (p2.isValid ? p2 : p0));
        }

        /**
         * Update adornments (specific to this element) to show a math-function curve in the plot.
         * The maximum number of regression lines this element controls is:
         *      (number of cells * number of numeric attributes on one axis * number of categories in legend )
         */
        calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
            "use strict";

            var adornsArray = ioAdornArray,
                plotViewPaper = this.plotShaper.adornments.paper,
                plotElement = this,
                isUpdateOnly = false;

            // clear any old adornments from previous PlotFunctionElements
            if( adornsArray.length === this._desiredNumberOfAdornments() ) {     // ASSUMES one path adornment per formula when complete, this may change.
                isUpdateOnly = true;
            } else {
                this.removeGlow(); // remove the glow in case the curve is deleted; we will add the glow back in later if the curve is re-created.
                Dyn.plotUtil.removeAndEmptyArrayOfElements( adornsArray );
                let measuresArray = this.plotShaper.measures.getMeasuresArray( this.primaryType );
                measuresArray.length = 0; // remove old measures (values for summary table)
            }

            if( this.isInPlottableAxes && !isUpdateOnly ) {
                let buttonNum = 0,
                    buttonSize = Dyn.plotUtil.iconSize; // DON'T scale for touch devices, it is big enough
                adornsArray.push( this.makeToolButton( adornsArray, buttonNum++,
                    "axisPlus", Dyn.loc( "zoom in" ), Raphael.transformPath( Dyn.plotUtil.getPlusIcon( buttonSize ), "T17,15" ) ) );
                adornsArray.push( this.makeToolButton( adornsArray, buttonNum++,
                    "axisMinus", Dyn.loc( "zoom out" ), Raphael.transformPath( Dyn.plotUtil.getMinusIcon( buttonSize ), "T17,15" ) ) );
                adornsArray.push( this.makeToolButton( adornsArray, buttonNum++,
                    "resetToData", Dyn.loc( "reset view" ), Raphael.transformPath( Dyn.plotUtil.iconResetView, "T-9,-9s0.4,0.4" ) ) );
                adornsArray.push( this.makeToolButton( adornsArray, buttonNum++,
                    "resetToEval", Dyn.loc( "show all of curve on y axis" ), Raphael.transformPath( Dyn.plotUtil.getFitEquationIcon(), "T7,6" ) ) );
                Dyn.assert( this.buttons.kNumButtons === buttonNum, "number of buttons mismatch" );
            }
            this.buttonPlus = adornsArray[0] || null;
            this.buttonMinus = adornsArray[1] || null;
            this.buttonReset = adornsArray[2] || null;
            this.buttonFit = adornsArray[3] || null;


            function pathHoverInOrMove( event ) { // also handles mouseMove to update tooltip
                //console.log( "fx pathHoverInOrMove ", event.layerX, event.layerY );
                event.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault;
                var pathCover = this,
                    isInDrag = pathCover.data( "isInDrag" ),
                    pathElement = this.data( "pathElement" ),
                    pathColor = pathCover.data( "pathColor" ),
                    formulaIndex = pathCover.data( "formulaIndex" ),
                    paperXY = Dyn.plotUtil.hoverEventToPaperXY( event, pathCover ),
                    hoverPointElement = plotElement._getPointElement( adornsArray, formulaIndex, kHoverPointIndex ),
                    lastPointData = hoverPointElement.data( "pointData" ),
                    hoverPointData = getPointOnPath( pathCover, event, paperXY, kHoverPointIndex, lastPointData );
                if( hoverPointData.isChangedPoint( hoverPointElement ) ) {
                    hoverPointData.setElement( hoverPointElement ); // new hover point location
                    if( hoverPointData.isValid && !isInDrag ) {
                        hoverPointElement.attr( { cx: hoverPointData.viewX, cy: hoverPointData.viewY } );
                        hoverPointData.show();
                    } else {
                        hoverPointData.hide();
                    }
                }
                plotElement.addGlow( pathElement, pathColor, Dyn.plotUtil.functionGlowWidth );
            }

            function pathHoverOut( /* event  */ ) {
                var pathCover = this,
                    isInDrag = pathCover.data( "isInDrag" ),
                    formulaIndex = pathCover.data( "formulaIndex" ),
                    hoverPointElement = plotElement._getPointElement( adornsArray, formulaIndex, kHoverPointIndex ),
                    hoverPointData = hoverPointElement.data( "pointData" );
                hoverPointData.hide();
                if( !isInDrag ) {
                    plotElement.removeGlow();
                }
            }

            function pathDragStart( x, y, event ) {
                let pathCover = this;
                pathCover.data( "isInDrag", true );
                pathClickOrDrag( pathCover, event, true );
            }

            function pathDragMove( dx, dy, x, y, event ) {
                pathClickOrDrag( this, event, false );
            }

            function pathDragEnd() {
                let pathCover = this;
                pathCover.data( "isInDrag", false );
                plotElement.removeGlow();
                if( Dyn.toolTip.isMyElement( pathCover ) ) {
                    Dyn.toolTip.hide();
                }
            }

            function pathClickOrDrag( pathCover, event, isStart ) {
                var formulaIndex = pathCover.data( "formulaIndex" ),
                    // TO-DO: get correct coordinates of Paper.x in Firefox.
                    // NOTE: on FireFox when dragging over path, event.layerX seems to return 0,
                    // then when drag is not under path it returns the correct layerX.
                    // layerY, offsetX, offsetY are unchanged. When stopping in debugger,
                    // firefox magically fixes the event and gives a non-zero layerX. --Craig 11/21/2019
                    paperXY = Dyn.plotUtil.hoverEventToPaperXY( event, pathCover ),
                    clickPointElement = plotElement._getPointElement( adornsArray, formulaIndex, kClickPointIndex ),
                    hoverPointElement = plotElement._getPointElement( adornsArray, formulaIndex, kHoverPointIndex ),
                    hoverPointData = hoverPointElement.data( "pointData" ),
                    lastPointData = isStart ? hoverPointData : clickPointElement.data( "pointData" ),
                    clickPointData = getPointOnPath( pathCover, event, paperXY, kClickPointIndex, lastPointData );
                if( clickPointData.isChangedPoint( clickPointElement ) ) {
                    clickPointData.setElement( clickPointElement ); // new click point location
                    if( clickPointData.isValid ) {
                        hoverPointData.hide();
                        clickPointElement.attr( { cx: clickPointData.viewX, cy: clickPointData.viewY } );
                        clickPointData.show();
                    } else {
                        clickPointData.hide();
                    }
                }
                if( isStart ) {
                    plotElement.selectAdornment( formulaIndex );
                }
                showToolTipText( pathCover, event, clickPointData );
            }

            function pointHoverIn( event ) {
                var pointElement = this,
                    hoverPointData = this.data( "pointData" ),
                    pathCover = pointElement.data( "pathCover" ),
                    pathElement = pathCover.data( "pathElement" ),
                    pathColor = pathCover.data( "pathColor" );
                if( !hoverPointData.display ) { // if not already calculated by getPointOnPath()
                    hoverPointData.updateDisplay(
                        xAxisView.getAttribute(), yAxisView.getAttribute(),
                        getPointPrecisionX(), getPointPrecisionY() );
                }
                plotElement.addGlow( pathElement, pathColor, Dyn.plotUtil.functionGlowWidth );
                showToolTipText( pathCover, event, hoverPointData );
            }

            function pointHoverOut( /* event  */ ) {
                var pointElement = this,
                    pathCover = pointElement.data( "pathCover" ),
                    isInDrag = pathCover.data( "isInDrag" );
                if( Dyn.toolTip.isMyElement( pathCover ) && !isInDrag ) {
                    Dyn.toolTip.hide();
                }
                plotElement.removeGlow();
            }

            function getPointOnPath( coverElement, event, paperXY, pointIndex, lastPointData ) {
                function viewXtoPointOnPath( newViewX ) {
                    let newDataX = xAxisView.coordMap.inverseTransform( newViewX ),
                        pt = new FunctionPointData( newDataX,
                            evaluateAtX( newDataX, formulaObj, formulaScope ),
                            pointIndex );
                    pt.updateViewCoords( xAxisView, yAxisView );
                    pt.viewX = newViewX;
                    return pt;
                }

                let formulaIndex = coverElement.data( "formulaIndex" ),
                    formulaScope = coverElement.data( "formulaScope" ),
                    formulaObj = plotElement.formulas[formulaIndex],
                    xAttr = xAxisView.getAttribute(),
                    yAttr = yAxisView.getAttribute(),
                    pointData = new FunctionPointData( 0, 0, pointIndex );

                let xPrecision, yPrecision; // default to attribute precision if invalid point
                if( formulaObj.compiledFormula ) { // don't evaluate if formula restored but not yet parsed
                    pointData = plotElement.findNearestPointOnFx( paperXY, viewXtoPointOnPath, lastPointData );
                    if( pointData.isValid ) {
                        let xOpts = {};
                        // get x,y at the right precision
                        pointData.dataX = xAxisView.snapIfCloseToTickmark( pointData.dataX, Dyn.plotUtil.functionSnapDist );
                        pointData.dataX = Dyn.mathUtil.roundToScale( pointData.dataX, getAxisScale( xAxisView ), xOpts );
                        pointData.dataY = evaluateAtX( pointData.dataX, formulaObj, formulaScope );
                        pointData.updateViewCoords( xAxisView, yAxisView );
                        // get number of decimal places for formatting numbers
                        xPrecision = xOpts.precision;
                        yPrecision = getPointPrecisionY();
                    }
                }
                pointData.updateDisplay( xAttr, yAttr, xPrecision, yPrecision );
                return pointData; // caller should check pointData.isValid
            }

            function getAxisScale( axisView ) {
                const extraPrecision = 10; // one more decimal place to ensure enough precision even with rounding
                return Math.abs( Dyn.mathUtil.invertSafely( axisView.coordMap.scale * extraPrecision ) );
            }

            function getPointPrecisionX() {
                // get precision to use for any arbitrary point on curve, under mouse, for axisValueToString()
                // note: must be syncronized with getPointOnPath()
                let xPixelScale = getAxisScale( xAxisView ),
                    xOpts = {};
                Dyn.mathUtil.roundToScale( 0, xPixelScale, xOpts ); // get precision for yPixelScale
                return xOpts.precision;
            }

            function getPointPrecisionY() {
                // get precision to use for any arbitrary point on curve, under mouse, for axisValueToString()
                let yPixelScale = getAxisScale( yAxisView ),
                    yAttr = yAxisView.getAttribute(),
                    yOpts = {};
                Dyn.mathUtil.roundToScale( 0, yPixelScale, yOpts ); // get precision for yPixelScale
                return Math.max( yOpts.precision, yAttr.getNumericDecimals() ) + Dyn.preferences.getPrecision();
            }

            function showToolTipText( coverElement, event, pointData ) {
                let newTip = Dyn.loc( "({{x}}, {{y}})", pointData.display ),
                    oldTip = coverElement.data( "tooltip" );
                coverElement.data( "tooltip", newTip );
                if( !pointData.isValid ) {
                    Dyn.toolTip.hide();
                } else if( Dyn.toolTip.isMyElement( coverElement ) ) {
                    // reduce updates while dragging; only update tip if significant text change
                    if( newTip !== oldTip ) {
                        Dyn.toolTip.update( coverElement, event, { x: pointData.viewX, y: pointData.viewY } );
                    }
                } else {
                    Dyn.toolTip.show( coverElement, event, { x: pointData.viewX, y: pointData.viewY } );
                }
            }

            function evaluateAtX( x, formulaObj, scope ) {
                formulaObj.getPlotFunctionScope( scope, x ); // define attributes and "x" at current x value
                return formulaObj.evaluate( scope, 'number' ); // evaluate and return an error if result is not type 'number'
            }

            function getPointData( formulaIndex, pointIndex, optionalNewDataX ) {
                // get or create the point data at the given index, being careful not to change
                //  the last dataX if it exists.
                let e = plotElement._getPointElement( adornsArray, formulaIndex, pointIndex ),
                    p = (e ? e.data( "pointData" ) : new FunctionPointData( 0, 0, pointIndex ));
                if( p.isIntercept && Dyn.mathUtil.isFinite( optionalNewDataX ) ) {
                    p.dataX = optionalNewDataX;
                }
                Dyn.assert( p.isElementValidOrNull() );
                return p;
            }

            function addPointToCurve( pathSet, pathCover, p /* class FunctionPointData */ ) {
                // create a visible point element from the point data, and add to the function line's set of elements.
                p.setElement( plotViewPaper.circle( p.viewX, p.viewY, Dyn.plotUtil.functionPointRadius ) );
                pathSet.push( p.el );
                if( !Dyn.environment.isTouchDevice() ) {
                    if( p.isHoverPoint ) {
                        Dyn.plotUtil.disableMouseOverEvents( p.el );
                    } else {
                        p.el.hover( pointHoverIn, pointHoverOut )
                            .drag( pathDragMove.bind( pathCover ), pathDragStart.bind( pathCover ), pathDragEnd.bind( pathCover ) );
                    }
                }
            }

            this.initRange( xAxisView, yAxisView );
            if( !this.isInPlottableAxes ) {
                return;
            }

            let rangeX = xAxisView.getNumericAxisRange(),
                viewRangeX = new Dyn.mathUtil.Range( xAxisView.bounds.left(), xAxisView.bounds.right() ),
                clipRectString = Dyn.plotView.getPlotInteriorClipRect(),
                dataSet = this.plotShaper.dataSet;

            this.formulas.forEach( ( formulaObj, formulaIndex ) => {
                if( this.isUpdateToUncompiledFunctionsOnly && formulaObj.compiledFormula ) {
                    return;
                }
                let scope = formulaObj.findDependencies( dataSet ), // parse formula if changed
                    roots = formulaObj.rootsAndInterceptsOfFx( scope ); // find finite numeric roots if any
                let canEvaluate = !(formulaObj.isEmptyFormula() || formulaObj.parseError),
                    formulaRange = this.formulaRanges[formulaIndex],
                    pathSet = this._getPathSet( adornsArray, formulaIndex ),
                    pathElement = this._getPathElement( adornsArray, formulaIndex ),
                    pathCover = this._getCoverElement( adornsArray, formulaIndex ),
                    points = [ // last hover and click point always exist, but may be hidden
                        getPointData( formulaIndex, kHoverPointIndex ), // hoverPointElement
                        getPointData( formulaIndex, kClickPointIndex ), // clickPointElement
                    ],
                    pathString = "",
                    pathColor = formulaObj.lineColor;
                // add points for roots and axis intercepts
                roots.forEach( x => points.push( getPointData( formulaIndex, points.length, x ) ) );
                if( canEvaluate ) {
                    // get the path string for this f(x) curve, and update this.plotRange and formulaRange
                    pathString = this.calcPathString( rangeX, viewRangeX, xAxisView, this.plotRange, formulaRange, formulaObj, function( pointData, dataX ) {
                        pointData.dataX = dataX;
                        pointData.dataY = evaluateAtX( dataX, formulaObj, scope );
                        pointData.updateViewCoords( xAxisView, yAxisView );
                    } );
                    formulaObj.evalCountStart();
                    points.forEach( p => {
                        p.dataY = evaluateAtX( p.dataX, formulaObj, scope );
                        p.updateViewCoords( xAxisView, yAxisView );
                        if( p.isIntercept && !p.display ) { // update intercept display values for Summary View
                            p.updateDisplay( xAxisView.getAttribute(), yAxisView.getAttribute(),
                                getPointPrecisionX(), getPointPrecisionY() );
                        }
                    } );
                    if( PlotFunctionElement.debugLogEvalCount ) {
                        console.log( "fx evaluations", formulaObj.evalCount(), "for points on function", formulaObj.nerdamerString );
                    }
                }
                if( pathString.length < 1 ) {
                    // there were no plotable points, create an empty path as a placeholder for this function
                    pathString = "M0,0";
                    pathElement = plotViewPaper.path( "M0,0" );
                    pathCover = plotViewPaper.path( "M0,0" );
                }
                if( isUpdateOnly ) {
                    // move path to new position
                    let animationTime = (noAnimation ? 0 : (this.isUpdateToConstantsOnly ? Dyn.plotUtil.functionFastAnimationTimeMs : Dyn.plotUtil.adornAnimationTimeMs));
                    // standard animation
                    pathElement.animate( { path: pathString }, animationTime, Dyn.plotUtil.adornAnimationEasing );
                    pathCover.animate( { path: pathString }, animationTime, Dyn.plotUtil.adornAnimationEasing );
                    points.forEach( ( p, pi ) => {
                        if( p.el ) {  // update existing point
                            p.el.animate( {
                                cx: p.viewX,
                                cy: p.viewY
                            }, animationTime, Dyn.plotUtil.adornAnimationEasing );
                        } else {      // line is new but point is added (e.g. extra root because of formula change)
                            addPointToCurve( pathSet, pathCover, p );
                        }
                    } );
                    // remove old points in this formula, if now we have fewer roots, etc.
                    for( let i = pathSet.length - 1; i > 0; --i ) {
                        let pointData = pathSet[i].data( "pointData" );
                        if( pointData && !points.includes( pointData ) ) {
                            let removedElement = pathSet.splice( i, 1 );
                            removedElement.remove(); // remove from paper
                        }
                    }
                } else {
                    // create one set of elements for this function, no animation
                    pathSet = plotViewPaper.set();
                    pathElement = plotViewPaper.path( pathString );
                    pathCover = plotViewPaper.path( pathString )
                        .drag( pathDragMove, pathDragStart, pathDragEnd );
                    if( !Dyn.environment.isTouchDevice() ) {
                        pathCover.hover( pathHoverInOrMove, pathHoverOut )
                            .mousemove( pathHoverInOrMove );
                    }
                    adornsArray.push( pathSet );
                    pathSet.push( pathElement, pathCover );
                    points.forEach( ( p, pi ) => {
                        addPointToCurve( pathSet, pathCover, p );
                    } );
                }

                // adornment updates common to new or updated functions
                pathElement.attr( {
                    stroke: pathColor,
                    "stroke-width": Dyn.plotUtil.functionCurveWidth,
                    "stroke-linecap": "round",
                    'clip-rect': clipRectString
                } ); // clip to plot interior
                let coverWidth = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.functionCoverWidth ),
                    isSelectedFormula = (formulaIndex === this.selectedFormulaIndex);
                pathCover   // transparent cover with wider target area
                    .attr( Dyn.debugViewRects ?
                        { 'clip-rect': clipRectString, "stroke-width": coverWidth, stroke: "orange", opacity: 0.5 } :
                        {
                            'clip-rect': clipRectString,
                            "stroke-width": coverWidth,
                            stroke: Dyn.plotUtil.transparentColor
                        } )
                    .data( "pathColor", pathColor )
                    .data( "pathElement", pathElement )
                    .data( "formulaScope", scope )
                    .data( "formulaIndex", formulaIndex );
                points.forEach( p => {
                    p.el.data( "pathCover", pathCover ) // note: has extra data referenced by point
                        .attr( {
                            stroke: pathColor,
                            fill: (p.isIntercept ? pathColor : "none") /*Dyn.plotUtil.functionPointColor*/,
                            'clip-rect': clipRectString
                        } );
                    p.updateVisibility( isSelectedFormula && canEvaluate );
                } );

                formulaRange.formatRange( xAxisView, yAxisView );
                let newFormulaMessage = plotElement.getFormulaMessage( formulaIndex );
                if( newFormulaMessage.fullString !== plotElement.messages[formulaIndex] ) { // if formula message has changed
                    mediator.publish( "plot:plotFunctionMessage", newFormulaMessage ); // notify Model Functions panel
                    plotElement.messages[formulaIndex] = newFormulaMessage.fullString; // save so we can publish once only
                }
                this.addToMeasures( formulaIndex, xAxisView, yAxisView, formulaObj, scope, points );
                //console.log( "PlotFunctionElement curve updated:", formulaIndex,
                //    this.formulaRanges[ formulaIndex ] && this.formulaRanges[ formulaIndex ].stringX,
                //    this.formulaRanges[ formulaIndex ] && this.formulaRanges[ formulaIndex ].stringY, formulaObj.getLastErrorString() );
            }, this );
            this.isUpdateToConstantsOnly = false;           // clear flag set by last update of this.setFormula();
            this.isUpdateToUncompiledFunctionsOnly = false; // clear flag set by last update of this.setFormula();
            Dyn.assert( adornsArray.length === this._desiredNumberOfAdornments() );
        }

// Dyn.PlotFunctionElement.prototype.resetMeasures = function() {
//     let plotMeasures = this.plotShaper.measures,
//         measuresArray = plotMeasures.getMeasuresArray( this.primaryType );
//     measuresArray.length = 0; // remove old measures (values for summary table)
// };

        /** add a measure value for our plot summary table */
        addToMeasures( formulaIndex, xAxisView, yAxisView, formulaObj, scope, points ) {
            let plotMeasures = this.plotShaper.measures,
                measuresArray = plotMeasures.getMeasuresArray( this.primaryType ),
                xAttr = xAxisView.getAttribute(),
                yAttr = yAxisView.getAttribute(),
                formulaID = formulaObj.id,
                modelNumber = formulaIndex + 1,
                oldMeasureIndex = measuresArray.findIndex( m => m.modelNumber === modelNumber );

            // create "m=20.3, b=0.132" from scope={ m:20.3, x=-1, b=0.121999999999999 }
            let constantsString = "";
            for( let c in scope ) {
                if( scope.hasOwnProperty( c ) ) {
                    if( c !== "x" ) { // ignoring special 'x' parameter
                        let spacer = (constantsString.length > 0) ? ', ' : '',
                            value = Dyn.mathUtil.roundToDecimalPlaces( scope[c], Dyn.mathUtil.kPrecision );
                        constantsString += (spacer + c + "=" + value);
                    }
                }
            }

            // create points string for intercepts and click point (if any)
            let pointsString = "";
            points.forEach( p => {
                if( p.isValid && p.isIntercept && p.display ) {
                    let spacer = (pointsString.length > 0) ? ' ' : '',
                        x = p.display.x, y = p.display.y;
                    pointsString += (spacer + Dyn.loc( "({{x}},{{y}})", { x: x, y: y } ));
                }
            } );

            /**
             * Class that holds a model function measure.
             * Each measure is a value in one of the Summary View tables, along with the metadata necessary to
             * determine which table, row, and column this value should appear in.
             */
            class modelFunctionMeasure {
                constructor( modelNumber, name, value ) {
                    this.setID = "modelFunction"; // which table
                    this.modelNumber = modelNumber; // which row
                    this.measureName = name; // which column
                    this.value = value; // cell value
                }
            }

            // add these 5 measures as a row of the model function table in the summary tables
            let newMeasures = [];
            newMeasures.push( new modelFunctionMeasure( modelNumber, Dyn.loc( "y" ), (yAttr ? yAttr.name : '') ) );
            newMeasures.push( new modelFunctionMeasure( modelNumber, plotMeasures.getNameOfFormulaMeasure(), formulaObj.latexString ) );
            newMeasures.push( new modelFunctionMeasure( modelNumber, Dyn.loc( "x" ), (xAttr ? xAttr.name : '') ) );
            newMeasures.push( new modelFunctionMeasure( modelNumber, Dyn.loc( "Constants" ), constantsString ) );
            newMeasures.push( new modelFunctionMeasure( modelNumber, Dyn.loc( "Intercepts" ), pointsString ) );
            newMeasures.push( new modelFunctionMeasure( modelNumber, plotMeasures.getNameOfColorMeasure(), formulaObj.lineColor ) );
            plotMeasures.addToOrUpdateMeasuresArray( measuresArray, newMeasures, oldMeasureIndex )
        }

        calcPathString( dataRangeX, viewRangeX, xAxisView, plotRange, formulaRange, formulaObj, calcPointOnCurve ) {
            // to avoid path string parsing errors by Raphael/SVG, clip very small or large values to these points, which should be offscreen anyway
            const minViewCoord = -1e6,
                maxViewCoord = 1e6;
            let pathString = "",
                plotElement = this;

            // Note: all angles are in Radians
            function calcPointAndDeltaSlopeInView( newDeltaViewX ) {
                deltaViewX = newDeltaViewX;
                deltaDataX = xAxisView.coordMap.inverseScaleAbs( deltaViewX );
                deltaAngle = Math.NaN;
                calcPointOnCurve( pt, lastPt.dataX + deltaDataX );
                if( pt.isValid && lastPt.isValid ) {
                    // slope Angle in Radians in View coordinates (ARV), e.g. 0 radians for horizontal line
                    // TODO: make sure this is valid for high to low axes
                    pt.slopeARV = Math.atan2( lastPt.viewY - pt.viewY, pt.viewX - lastPt.viewX );
                    if( Number.isFinite( lastPt.slopeARV ) ) {
                        deltaAngle = Math.abs( pt.slopeARV - lastPt.slopeARV );
                        if( deltaViewX > 1 ) { // adjust angle for long runs
                            deltaAngle *= deltaViewX;
                        }
                    }
                }
            }

            function debugLogCalculation( pt, evalCount, str ) {
                if( PlotFunctionElement.debugLogEveryEval ) {
                    console.log( "fx pt", ptCount, "x,y:", pt.dataX, pt.dataY, '+vx:', deltaViewX,
                        'deltaAngle=' + (Number.isFinite( deltaAngle ) ? deltaAngle.toFixed( 5 ) : deltaAngle),
                        "try " + evalCount + " " + str );
                }
            }

            formulaObj.evalCountStart();
            let hasDiscontinuity = false,   // path should be invisible where not continuous
                deltaViewX = Dyn.plotUtil.fxInitialDeltaViewX,
                deltaDataX = xAxisView.coordMap.inverseScaleAbs( deltaViewX ),
                deltaAngle,
                lastPt = new FunctionPointData( dataRangeX.min - deltaDataX ),
                pt = new FunctionPointData( lastPt.dataX ),
                ptCount = 0;
            calcPointOnCurve( lastPt, lastPt.dataX );
            // evaluate for points along X axis, spacing of points will vary according to slope
            do {
                let evalCount = 0;
                calcPointAndDeltaSlopeInView( deltaViewX );
                debugLogCalculation( pt, ++evalCount, "" );
                if( pt.isValid ) {
                    // if needed, expand distance between points, so curve > minAngle
                    //  but not more than maxDeltaViewX points from the last point, for efficiency
                    while( deltaAngle < Dyn.plotUtil.fxMinAngle && deltaViewX < 0.5 * Dyn.plotUtil.fxMaxDeltaViewX ) {
                        calcPointAndDeltaSlopeInView( deltaViewX * 2 );
                        debugLogCalculation( pt, ++evalCount, (deltaAngle >= Dyn.plotUtil.fxMinAngle ? "angle>=min " : "") + (deltaViewX >= 0.5 * Dyn.plotUtil.fxMaxDeltaViewX ? "dx>max/2" : "") );
                    }
                    // if needed, shrink distance between points, so curve < maxAngle
                    //  but not more than maxDeltaViewX points from the last point, for efficiency
                    while( deltaAngle > Dyn.plotUtil.fxMaxAngle && deltaViewX > Dyn.plotUtil.fxMinDeltaViewX * 2 ) {
                        calcPointAndDeltaSlopeInView( deltaViewX / 2 );
                        debugLogCalculation( pt, ++evalCount, (deltaAngle <= Dyn.plotUtil.fxMaxAngle ? "angle<=max " : "") + (deltaViewX <= 2 * Dyn.plotUtil.fxMinDeltaViewX ? "dx>min*2 " : "")
                            + (deltaAngle > Dyn.plotUtil.fxDiscontinuityAngle ? "+hasDiscontinuity because of angle" : "") );
                    }
                    if( deltaAngle > Dyn.plotUtil.fxDiscontinuityAngle ) {
                        hasDiscontinuity = true;
                    }
                    let debugShowAsDash = false; //Dyn.mathUtil.isEven(ptCount); // uncomment to show individual points on line for debugging
                    let sx = Dyn.mathUtil.clipToMinMax( pt.viewX.toFixed( 1 ), minViewCoord, maxViewCoord ),
                        sy = Dyn.mathUtil.clipToMinMax( pt.viewY.toFixed( 1 ), minViewCoord, maxViewCoord );
                    //Dyn.assertFiniteNumeric( Number(sx) );
                    //Dyn.assertFiniteNumeric( Number(sy) );
                    pathString += ((pathString.length === 0 || hasDiscontinuity || debugShowAsDash) ? "M" : "L") + sx + "," + sy;
                    // expand ranges
                    plotRange.evalX.addPoint( pt.dataX );
                    plotRange.evalY.addPoint( pt.dataY );
                    formulaRange.addToRange( pt.dataX, pt.dataY, hasDiscontinuity );
                    hasDiscontinuity = false;
                } else {
                    formulaRange.addUndefinedPoint();
                    hasDiscontinuity = true;
                }
                // increment point
                ++ptCount;
                lastPt.copyData( pt );
                lastPt.slopeARV = pt.slopeARV;
            } while( ptCount < 2 || pt.dataX < dataRangeX.max );

            if( PlotFunctionElement.debugLogEvalCount ) {
                console.log( "fx evaluations", formulaObj.evalCount(), "for", ptCount, "points in curve, width", Math.round( viewRangeX.width() ), "function", formulaObj.nerdamerString );
            }
            return pathString;
        }

        /**
         * Make a button for the function curves.
         * TODO: move this to Dyn.plotUtil and use for both the MapElement and PlotFunctionElement.
         * @param ioAdornArray
         * @param buttonNum
         * @param buttonTypeString
         * @param toolTipText
         */
        makeToolButton( ioAdornArray, buttonNum, buttonTypeString, toolTipText, path ) {
            "use strict";

            function buttonHoverIn( e ) {
                e.currentTarget.style.cursor = Dyn.plotUtil.cursorPointer;
                this.attr( { fill: Dyn.plotUtil.mapButtonHover, stroke: Dyn.plotUtil.handleDownColor } );
                const toolTipOffset = 20;
                if( this.data( "tooltip" ) )
                    Dyn.toolTip.show( this, e, { x: this.attrs.x + toolTipOffset, y: this.attrs.y + toolTipOffset } );
            }

            function buttonHoverOut( e ) {
                e.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault;
                this.attr( { fill: Dyn.plotUtil.mapButtonNormal, stroke: Dyn.plotUtil.axisButtonUpColor } );
                if( Dyn.toolTip.isMyElement( this ) )
                    Dyn.toolTip.hide();
            }

            // function iconHoverIn( e ){
            //     this.attr( { fill: Dyn.plotUtil.axisButtonDownColor, stroke: Dyn.plotUtil.axisButtonDownColor } );
            // }
            // function iconHoverOut( e ){
            //     this.attr( { fill: Dyn.plotUtil.axisButtonUpColor, stroke: Dyn.plotUtil.axisButtonUpColor } );
            // }
            function buttonClick( /*e*/ ) {
                var thisButton = this,
                    buttonType = thisButton.data( "type" );
                mediator.publish( "axis:clickButton", { type: buttonType } );
            }

            var plotElement = this,
                plotPaper = this.plotShaper.adornments.paper,
                isActiveTool = false, // todo: caller should determine this.
                ariaLabel = toolTipText,
                left = Dyn.plotUtil.mapButtonleft,
                top = this.buttons.top( buttonNum ),
                set = plotPaper.set(),
                outline = plotPaper.rect( left, top, this.buttons.width, this.buttons.height, this.buttons.radius )
                    .attr( {
                        stroke: plotElement.getOutlineStrokeColor( isActiveTool ),
                        fill: Dyn.plotUtil.mapButtonNormal
                    } )
                    .data( "type", buttonTypeString )
                    .data( "tooltip", toolTipText )
                    //.hover( buttonHoverIn, buttonHoverOut )
                    .click( buttonClick ),
                symbol = plotPaper.path( Raphael.transformPath( path, "T" + (left) + "," + (top) ) )
                    .attr( { stroke: "none", fill: plotElement.getSymbolFillColor( isActiveTool ) } );
            // .hover( iconHoverIn, iconHoverOut );
            outline.node.setAttribute( "role", "button" );

            // make element keyboard accessible.
            let keyDownHandler = function( e ) {
                switch( e.key ) {
                    case "Enter":
                        if( buttonClick ) {
                            buttonClick.call( outline, e );
                        }
                        break;
                }
            };
            Dyn.plotUtil.setKeyboardFocusable( outline, keyDownHandler, !!ariaLabel ? ariaLabel : false );

            // button is a set with an outline and a symbol
            if( !Dyn.environment.isTouchDevice() ) {
                outline.hover( buttonHoverIn, buttonHoverOut );
            }
            Dyn.plotUtil.disableMouseOverEvents( symbol ); // so it does not interfere with outline's buttonHoverIn()
            set.push( outline ); // [0]
            set.push( symbol );  // [1]
            return set;
        }

        getOutlineStrokeColor( isActiveTool ) {
            "use strict";
            return isActiveTool ? Dyn.plotUtil.axisButtonDownColor : Dyn.plotUtil.axisButtonUpColor;
        }

        getSymbolFillColor( isActiveTool ) {
            "use strict";
            return isActiveTool ? Dyn.plotUtil.axisButtonDownColor : Dyn.plotUtil.axisButtonUpColor;
        }

        /**
         * {OVERRIDE} Deselect any selected adornments in this plot element.
         */
        clearSelectedAdornments() {
            "use strict";
            let oldFormulaIndex = this.selectedFormulaIndex;

            this.selectedFormulaIndex = -1;

            // clear selection indicators in fx line and fx control panel
            if( Dyn.mathUtil.isInIntegerRange( oldFormulaIndex, 0, this.formulas.length ) ) {
                let oldFormulaID = this.formulas[oldFormulaIndex].id;
                this.showHidePointsOfSelectedLine();
                mediator.publish( "plot:plotFunctionSelected", { id: oldFormulaID, isSelected: false } );
            }
        }

        /**
         * Select an adornment.
         * @param adornmentIndex
         */
        selectAdornment( formulaIndex ) {
            "use strict";

            // set selection indicators in fx line and fx control panel
            if( Dyn.mathUtil.isInIntegerRange( formulaIndex, 0, this.formulas.length ) ) {
                let formulaID = this.formulas[formulaIndex].id;
                if( formulaIndex !== this.selectedFormulaIndex ) {
                    this.selectedFormulaIndex = formulaIndex;
                    this.plotShaper.clearSelectedAdornments( this ); // clear other adornment selections
                    this.showHidePointsOfSelectedLine(); // select points on this line
                    // tell formula panel to highlight the formula for the selected path
                    mediator.publish( "plot:plotFunctionSelected", { id: formulaID, isSelected: true } );
                }
            } else {
                this.clearSelectedAdornments();
            }
        }

        /**
         * Show the points of the selected function line, if they exist and are valid,
         * and hide the points on unselected functions. */
        showHidePointsOfSelectedLine() {
            let plotElement = this,
                adornsArray = this.getAdornmentsArray();

            this.formulas.forEach( ( fx, formulaIndex ) => {
                let isFormulaSelected = (formulaIndex === this.selectedFormulaIndex),
                    pathSet = this._getPathSet( adornsArray, formulaIndex );
                pathSet.forEach( ( el ) => {
                    // for any elements of the function set that are points
                    let pointData = el.data( "pointData" );
                    if( pointData ) {
                        pointData.updateVisibility( isFormulaSelected );
                    }
                }, this );
            }, this );
        }

        /**
         * Set or update the given formula in the plot. See also removeFormula()
         * When plot is updated, this.calcAdornments() will add/update/delete the matching function line.
         * @param formulaID
         * @param formulaString
         * @param formulaSyntax
         * @param constants
         * @param lineColor
         */
        setFormula( formulaID, formulaString, formulaSyntax, constants, lineColor, isSingleFunctionUpdate /*optional*/ ) {
            // IDs are strings, make it so, for consistency, before comparing or assigning to any formula
            // NOTE: ModelFunctions.jsx makes numeric IDs, but Dyn.FunctionUtil uses IDs as object keys which convert to strings.
            formulaID = "" + formulaID;
            let isUpdate = false,
                formulaIndex = this.formulaIDtoIndex( formulaID );

            // check parameters
            if( Dyn.mathUtil.isInIntegerRange( formulaIndex, 0, this.formulas.length ) ) {
                isUpdate = true;
            }
            if( !constants ) {
                constants = {};
            }
            if( !constants.hasOwnProperty( 'x' ) ) {
                constants.x = NaN; // define X for syntax checking
            }

            let newFormula = new Dyn.Formula( formulaString || "", formulaSyntax, constants, formulaID );
            newFormula.lineColor = lineColor;
            newFormula.formulaSyntax = formulaSyntax;
            if( formulaSyntax === "LaTeX" ) {
                newFormula.latexString = formulaString; // save for addToMeasures()
            }

            if( isUpdate ) {
                let oldFormula = this.formulas[formulaIndex];

                this.isUpdateToConstantsOnly = (newFormula.formulaString === oldFormula.formulaString && newFormula.lineColor === oldFormula.lineColor);
                this.isUpdateToUncompiledFunctionsOnly = Boolean( isSingleFunctionUpdate );
                this.formulas[formulaIndex] = newFormula;   // update old formula with new uncompiled formula
            } else {
                formulaIndex = this.formulas.length;
                this.formulas.push( newFormula );           // add additional formula
            }
            //console.log( "PlotFunctionElement.setFormula() "+formulaIndex+" of "+this.formulas.length+" '"+formulaString+"' => '"+newFormula.formulaString+"'", constants, lineColor );
        }

        /**
         * Remove the given formula and return the resulting number of formulas left.
         * @param formulaID
         * @returns {number}
         */
        removeFormula( formulaID ) {
            let formulaIndex = this.formulaIDtoIndex( formulaID );

            if( Dyn.mathUtil.isInIntegerRange( formulaIndex, 0, this.formulas.length ) ) {
                this.formulas.splice( formulaIndex, 1 );        // remove a formula
                //console.log( "PlotFunctionElement.setFormula() remove index:", formulaIndex, ", now ", this.formulas.length, " formulas" );
            }
            return this.formulas.length;
        }

        /**
         * Return the state of the given formula.
         * @param formulaID {number | string}
         * @returns {{id: *, message: string, minX}}
         */
//Dyn.PlotFunctionElement.prototype.getFormulaState = function( formulaID ) {
//    let formulaIndex = this.formulaIDtoIndex( formulaID );
//    this.getFormulaMessage( formulaIndex );
//};

        getFormulaMessage( formulaIndex ) {
            let formulaObj = this.formulas[formulaIndex],
                formulaRange = this.formulaRanges[formulaIndex],
                result = {
                    id: formulaObj.id,
                    message: "", // empty string if no errors
                    rangeX: "",
                    rangeY: "",
                    warning: "",
                    fullString: ""
                },
                formulaNotVisible = (
                    (this.plotRange.dataX.isFinite() && this.plotRange.dataY.isFinite() &&
                        formulaRange.countFinite > 0) &&
                    (this.plotRange.dataX.min > formulaRange.evalX.max ||
                        this.plotRange.dataX.max < formulaRange.evalX.min ||
                        this.plotRange.dataY.min > formulaRange.evalY.max ||
                        this.plotRange.dataY.max < formulaRange.evalY.min));

            function resultToString( result ) {
                // compare this string to old state to know if state has changed
                return result.id + "|" + result.message + "|" + result.rangeX + "|" + result.rangeY + "|" + result.warning;
            }

            // add formula's computed numeric range if any
            if( formulaRange ) {
                result.rangeX = formulaRange.stringX;
                result.rangeY = formulaRange.stringY;
            }

            // add formula's error message if any
            if( formulaObj ) {
                result.message = formulaObj.getLastErrorString( "notEvalErrors" );
            }
            if( !result.message ) {
                // if no other error, report that functions are not plottable
                if( !this.isInPlottableAxes ) {
                    result.message = Dyn.loc( "Need numeric axis on X and Y to plot functions" );
                } else if( formulaRange &&
                    (formulaRange.countAll > 0) && (formulaRange.countFinite === 0) ) {

                    result.message = Dyn.loc( "Formula is undefined across X-axis range" );
                }
            }
            // if formula is correct and there is plot function on the visualization, check if its in the
            // visible area, if not send the warning message.
            if( formulaNotVisible ) {
                result.warning = Dyn.loc( "The function is outside the plot viewing area." );
            }

            result.fullString = resultToString( result );

            return result;
        }

        getNumFormulas() {
            return this.formulas.length;
        }

        /**
         * convert a formula ID to a valid index into our list of formulas, or return the next available formula index
         * @param formulaID -- number or string
         * @param allowFailure -- if true, return -1 if not found, instead of next available formula index
         * @returns {number}
         */
        formulaIDtoIndex( formulaID, allowFailure ) {
            // IDs are strings, make it so, for consistency, before comparing or assigning to any formula
            formulaID = "" + formulaID;
            var formulaIndex = this.formulas.findIndex( formulaObj => (formulaObj.id === formulaID) );
            return ((formulaIndex === -1 && !allowFailure) ?
                this.formulas.length    // not found; return next available index, 0+
                : formulaIndex);       // return index of formula with matching ID
        }

        /**
         * Initialize the data and function evaluation ranges.
         * To be used to reset the ranges after every data or function change.
         * Caller should use this.plotRange.evalY.addPoint(), etc to expand the ranges once a finite
         *      numeric value has been calculated.
         * @param xAxisView
         * @param yAxisView
         */
        initRange( xAxisView, yAxisView ) {
            function newRange( oldRange ) {
                if( oldRange ) {
                    return new Dyn.mathUtil.Range( oldRange.min, oldRange.max );
                }
                return null;
            }

            this.plotRange.dataX = xAxisView.getNumericAxisRange();
            this.plotRange.dataY = yAxisView.getNumericAxisRange();
            this.plotRange.evalX = newRange( this.plotRange.dataX );
            this.plotRange.evalY = newRange( this.plotRange.dataY );
            this.formulaRanges = [];
            this.formulas.forEach( ( formula, index ) => {
                this.formulaRanges[index] = new FormulaRange();
            } )
        }

        /**
         * reset the axes to show all the function x,y points, or all x,y data points (toggle).
         * Note that the function is evaluated across the current X-axis range, so that after a
         * formula change this reset will typically only change the Y axis.
         */
        resetXYAxisRanges( subViewRef, type ) {
            "use strict";
            var xAxisView = subViewRef.xAxisView,
                yAxisView = subViewRef.yAxisView,
                userRangeX = xAxisView.numeric.userRange || xAxisView.getNumericAxisRange(), // the last user-specified range (final axis range not the sa
                userRangeY = yAxisView.numeric.userRange || yAxisView.getNumericAxisRange();

            switch( type ) {
                case "resetToData": {
                    // reset to show all XY data points
                    xAxisView.setUserCustomizedMinMax();
                    yAxisView.setUserCustomizedMinMax();
                }
                    break;
                case "resetToEval": {
                    xAxisView.setUserCustomizedMinMax( this.plotRange.evalX ); // reset to show all XY points of evaluated formula
                    yAxisView.setUserCustomizedMinMax( this.plotRange.evalY ); // reset to show all XY points of evaluated formula
                }
                    break;
                case "axisPlus": {
                    const zoomInFraction = 0.25;
                    let wx = userRangeX.width() * zoomInFraction,
                        wy = userRangeY.width() * zoomInFraction;
                    userRangeX.set( userRangeX.min + wx, userRangeX.max - wx );
                    userRangeY.set( userRangeY.min + wy, userRangeY.max - wy );
                    xAxisView.setUserCustomizedMinMax( userRangeX );
                    yAxisView.setUserCustomizedMinMax( userRangeY );
                }
                    break;
                case "axisMinus": {
                    const zoomOutFraction = 0.5;
                    let wx = userRangeX.width() * zoomOutFraction,
                        wy = userRangeY.width() * zoomOutFraction;
                    userRangeX.set( userRangeX.min - wx, userRangeX.max + wx );
                    userRangeY.set( userRangeY.min - wy, userRangeY.max + wy );
                    xAxisView.setUserCustomizedMinMax( userRangeX );
                    yAxisView.setUserCustomizedMinMax( userRangeY );
                }
                    break;
            }
        }

// hw mess    Dyn.EPlotChange parameter if returning true.
// hw mess     * @param subViewRef
// hw mess     * @param axisdata
// hw mess     * @returns {boolean} -- true if handled by a Dyn.PlotElement
// hw mess     */
        handleAxisButtonClick( ioWhatChanged, subViewRef, axisData ) {
            "use strict";
            var handled = false;

            switch( axisData.type ) {
                case "resetToData":
                case "resetToEval":
                case "axisPlus":
                case "axisMinus":
                    if( this.isInPlottableAxes ) {
                        this.resetXYAxisRanges( subViewRef, axisData.type );
                        ioWhatChanged.viewSize = true; // update plot to new axis position
                        handled = true;
                    }
                    break;
            }
            return handled;
        }

        /**
         * Tell plot elements to hide or show any controls.
         */
        setViewMode( mode ) {
            // NOTE: buttons may be null, that is OK
            let showControls = mode.showControls();
            Dyn.plotUtil.showOrHideElement( this.buttonPlus, showControls );
            Dyn.plotUtil.showOrHideElement( this.buttonMinus, showControls );
            Dyn.plotUtil.showOrHideElement( this.buttonReset, showControls );
            Dyn.plotUtil.showOrHideElement( this.buttonFit, showControls );
        }
    } // class PlotFunctionElement
    Dyn.PlotFunctionElement = PlotFunctionElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsPlotFunction, Dyn.PlotFunctionElement );
} // wrap DynPlotFunctionElement
